import dayjs, { Dayjs } from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import weekday from 'dayjs/plugin/weekday'
import { t } from 'i18next'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { JournalRythmCt } from '../../../model/enums/enums'
import { learningJournal } from '../../../model/learnJournal.model'
import { Apprentice } from '../../../model/person.model'
import { useAppSelector } from '../../../redux/hooks'
import { AuthRoutes } from '../../../router/routes'
import { findApprenticeById } from '../../../util/arraySearch'
import { getPersonFullName } from '../../../util/getPersonName'
import { actionBodyAdd } from '../../util/data-table/cell-body/ActionBody'
import { AvatarCell } from '../../util/data-table/cell-body/FullNameApprenticeBody'
import { InactiveRowStyle } from '../../util/data-table/row-class/InactiveRow'

interface ApprenticeIdWithMissingJournal {
  apprentice_id?: number
  full_name?: string
  missingDate: Dayjs
}

dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(weekday)

export const MissingJournalsTable = (props: {
  currentApprentice: Apprentice | undefined
  periodFrom: Dayjs
  periodTo: Dayjs
  journals: learningJournal[]
}): JSX.Element => {
  const { loadedBasicInformation } = useAppSelector((state) => state.user)
  const apprentices = useAppSelector((state) =>
    state.apprentices.filter((app) => app.is_active)
  )

  const [dataTableInformation, setDataTableInformation] = useState<
    ApprenticeIdWithMissingJournal[]
  >([])
  const [expandedRows, setExpandedRows] = useState<any>([])

  const navigate = useNavigate()
  const apprenticeAvatar = (
    personWithMissingJournal: ApprenticeIdWithMissingJournal
  ) => {
    const person = findApprenticeById(
      personWithMissingJournal.apprentice_id,
      apprentices
    )
    const numEntries = dataTableInformation.filter(
      (val) => val.apprentice_id === personWithMissingJournal.apprentice_id
    ).length
    return (
      <div
        className={
          'inline-flex justify-content-center align-items-center gap-2'
        }
      >
        <div className={'min-width-240'}>{AvatarCell(person)}</div>
        <span className={'min-width-120'}>{`(${t<string>(
          `apprentice.journal_rythm.enum.${
            person?.apprentice?.journal_rythm || '0'
          }`
        )})`}</span>
        <span className={'col flex justify-content-start align-items-center'}>
          {t<string>('journal.missing_journals_period')}:{' '}
          <b className={'pl-1 pr-3'}>{numEntries}</b>
        </span>
      </div>
    )
  }

  useEffect(() => {
    if (loadedBasicInformation) {
      const finalApprenticeArray: ApprenticeIdWithMissingJournal[] = []
      if (props.currentApprentice) {
        const missing = calculateNumMissingJournals(props.currentApprentice)
        for (const m of missing) {
          const currentApprenticeCopy: ApprenticeIdWithMissingJournal = {
            apprentice_id: props.currentApprentice.apprentice?.apprentice_id,
            full_name: getPersonFullName(props.currentApprentice),
            missingDate: m,
          }
          finalApprenticeArray.push(currentApprenticeCopy)
        }
        setDataTableInformation(finalApprenticeArray)
        return
      }

      apprentices.forEach((app) => {
        const missing = calculateNumMissingJournals(app)
        for (const m of missing) {
          const currentApprenticeCopy: ApprenticeIdWithMissingJournal = {
            apprentice_id: app.apprentice?.apprentice_id,
            missingDate: m,
            full_name: getPersonFullName(app),
          }
          finalApprenticeArray.push(currentApprenticeCopy)
        }
      })

      setDataTableInformation(finalApprenticeArray)
    }
  }, [props.periodFrom, props.periodTo, props.currentApprentice])

  const calculateNumMissingJournals = (body: Apprentice) => {
    const contractStart = dayjs(body.apprentice?.contract_start)
    const contractEnd = dayjs(body.apprentice?.contract_end)
    let countStart = contractStart.isAfter(props.periodFrom)
      ? contractStart
      : props.periodFrom
    let countEnd = contractEnd.isBefore(props.periodTo)
      ? contractEnd
      : props.periodTo
    if (body.apprentice?.journal_start) {
      const journalStart = dayjs(body.apprentice.journal_start)
      countStart = countStart.isBefore(journalStart) ? journalStart : countStart
    }
    if (countStart > countEnd) {
      return []
    }
    const weekOrMonth =
      body.apprentice?.journal_rythm === JournalRythmCt.Monthly
        ? 'month'
        : 'week'
    countStart = countStart.startOf(weekOrMonth)
    countEnd = countEnd.endOf(weekOrMonth)
    const ljsInPeriod = props.journals.filter(
      (j) =>
        j.id_apprentice === body.apprentice?.apprentice_id &&
        dayjs(j.entry_date).isSameOrAfter(props.periodFrom, weekOrMonth) &&
        dayjs(j.entry_date).isSameOrBefore(props.periodTo, weekOrMonth)
    )
    let countStartLoopVariable = countStart
    const missing: Dayjs[] = []
    while (countStartLoopVariable.isBefore(countEnd)) {
      const res = ljsInPeriod.find((lj) =>
        dayjs(lj.entry_date).isSame(countStartLoopVariable, weekOrMonth)
      )
      if (!res) {
        missing.push(countStartLoopVariable)
      }
      countStartLoopVariable = countStartLoopVariable.add(1, weekOrMonth)
    }
    return missing
  }

  const missingDateinTable = (date: Dayjs, apprentice_id: number) => {
    const app = findApprenticeById(apprentice_id, apprentices)
    if (app?.apprentice?.journal_rythm === JournalRythmCt.Monthly) {
      const month = date.month()
      const firstDay = date
        .startOf('month')
        .format(t<string>('locales.dateFormat'))
      const lastDay = date
        .endOf('month')
        .format(t<string>('locales.dateFormat'))
      return `${t<string>(
        `locales.monthNames.${month}`
      )} (${firstDay} - ${lastDay})`
    }
    const weekBegin = date.weekday(0).format(t<string>('locales.dateFormat'))
    const weekEnd = date.weekday(6).format(t<string>('locales.dateFormat'))
    return `${t<string>(
      'core.calendary_week.short'
    )} ${date.week()} (${weekBegin} - ${weekEnd})`
  }

  const routeToJournal = (pWMJ: ApprenticeIdWithMissingJournal) => {
    navigate(AuthRoutes.addJournal, {
      state: {
        apprentice_id: pWMJ.apprentice_id,
        date: pWMJ.missingDate.toDate(),
      },
    })
  }

  return (
    <DataTable
      showGridlines
      stripedRows
      className={'p-datatable-sm ml-0 pl-0'}
      value={dataTableInformation}
      scrollable
      scrollHeight="600px"
      rowHover
      emptyMessage={t<string>('core.empty_message')}
      rowClassName={(r) => InactiveRowStyle(r)}
      expandableRowGroups
      groupRowsBy="apprentice_id"
      rowGroupMode="subheader"
      rowGroupHeaderTemplate={(val: ApprenticeIdWithMissingJournal) =>
        apprenticeAvatar(val)
      }
      sortMode="single"
      sortOrder={-1}
      onRowToggle={(e) => setExpandedRows(e.data)}
      expandedRows={expandedRows}
      removableSort
    >
      <Column
        header={t<string>('core.apprentices')}
        field={'full_name'}
        sortField={'full_name'}
        sortable
      />
      <Column
        header={t<string>('core.handin_period')}
        field="missingDate"
        body={(e: ApprenticeIdWithMissingJournal) =>
          missingDateinTable(e.missingDate, e.apprentice_id)
        }
      >
        {' '}
      </Column>
      <Column body={actionBodyAdd(routeToJournal)} style={{ width: '1em' }} />
    </DataTable>
  )
}